import { ReloadOutlined, SyncOutlined } from "@ant-design/icons"
import { Alert, Button, Empty, Flex, Spin, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { RoutesPaths } from "../../constants/routes.ts"
import { MapIllustration } from "./MapIllustration.tsx"

const { Text } = Typography

/**
 * Six pure, presentational states for the Global Map surface — no Redux, no
 * API calls, no router. Task 20 wires these into the live page against real
 * fetch/staleness state; here they only take the props they need to render.
 */

export function MapLoading() {
  const { t } = useTranslation()

  return (
    <Flex
      vertical
      align="center"
      justify="center"
      gap={8}
      role="status"
      aria-live="polite"
      className="min-h-[220px] px-6 py-6 text-center"
    >
      <Spin size="large" />
      <Text type="secondary">{t("globalMap.states.loading")}</Text>
    </Flex>
  )
}

interface MapNoResultsProps {
  onReset?: () => void
  /** Every layer is off, so there is nothing to ask for rather than nothing found. */
  noLayers?: boolean
}

export function MapNoResults({ onReset, noLayers = false }: MapNoResultsProps) {
  const { t } = useTranslation()

  // A compact card rather than a full-bleed `Empty`: this overlays a live
  // map, so it has to read as a small filter-state notice sitting on top of
  // it, never as a component that has taken the map over.
  return (
    <div className="flex max-w-[300px] flex-col items-center gap-2 text-center">
      <MapIllustration width={108} />
      <div className="text-[15px] font-semibold text-[#0F172A]">
        {t(
          noLayers
            ? "globalMap.states.noResults.noLayersTitle"
            : "globalMap.states.noResults.title"
        )}
      </div>
      <div className="text-[13px] leading-snug text-slate-500">
        {t(
          noLayers
            ? "globalMap.states.noResults.noLayersDescription"
            : "globalMap.states.noResults.description"
        )}
      </div>
      {onReset && (
        <Button
          onClick={onReset}
          className="mt-1 h-9 rounded-lg border-[#E2E8F0] text-[13px] font-medium text-slate-700"
        >
          {t("globalMap.states.noResults.reset")}
        </Button>
      )}
    </div>
  )
}

export function MapNoData() {
  const { t } = useTranslation()

  return (
    <Empty
      description={
        <Flex vertical align="center" className="text-center py-1">
          <Text type="secondary" strong>
            {t("globalMap.states.noData.title")}
          </Text>
          <Text type="secondary" className="mt-1 max-w-xs">
            {t("globalMap.states.noData.description")}
          </Text>
        </Flex>
      }
    />
  )
}

interface MapPartialGeocodingProps {
  unmapped: number
  onViewRecords?: () => void
}

// Stub target for this increment — Task 20's live page decides whether this
// should deep-link with the map's current filters instead of the bare list.
export function MapPartialGeocoding({
  unmapped,
  onViewRecords
}: MapPartialGeocodingProps) {
  const { t } = useTranslation()

  return (
    <Alert
      type="warning"
      showIcon
      message={t("globalMap.states.partialGeocoding.title", {
        count: unmapped
      })}
      action={
        <a href={RoutesPaths.project.list} onClick={onViewRecords}>
          {t("globalMap.states.partialGeocoding.viewRecords")}
        </a>
      }
    />
  )
}

interface MapStaleProps {
  onRefresh: () => void
}

export function MapStale({ onRefresh }: MapStaleProps) {
  const { t } = useTranslation()

  return (
    <Alert
      type="info"
      showIcon
      message={t("globalMap.states.stale.title")}
      action={
        <Button size="small" icon={<SyncOutlined />} onClick={onRefresh}>
          {t("globalMap.states.stale.refresh")}
        </Button>
      }
    />
  )
}

interface MapProviderErrorProps {
  onRetry: () => void
}

// Renders in place of the map only — the rest of the page (sidebar, filters)
// stays mounted and usable around it.
//
// Fills the map rectangle's full height and centres on both axes, so the
// state reads as the map surface's own content rather than as a block pinned
// to its top edge. The `Result` component this replaced carried a small
// warning glyph and no explanatory copy; the approved design wants the same
// folded-map illustration the details column uses, a reason, and the retry.
export function MapProviderError({ onRetry }: MapProviderErrorProps) {
  const { t } = useTranslation()

  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-4 px-6 py-10 text-center"
      data-testid="global-map-provider-error"
    >
      <MapIllustration width={200} />
      <div className="max-w-[360px]">
        <div className="text-[20px] font-semibold text-[#0F172A]">
          {t("globalMap.states.providerError.title")}
        </div>
        <div className="mt-2 text-[13px] leading-relaxed text-slate-500">
          {t("globalMap.states.providerError.description")}
        </div>
      </div>
      <Button
        type="primary"
        icon={<ReloadOutlined />}
        onClick={onRetry}
        className="h-10 rounded-lg px-5 text-[13px] font-medium !shadow-[0_4px_12px_rgba(42,125,140,0.25)]"
      >
        {t("globalMap.states.providerError.retry")}
      </Button>
    </div>
  )
}
